// src/routes/$locale/collections/index.tsx — base /collections route (no net param)
import { createFileRoute } from '@tanstack/react-router'
import { getCollections } from '@/lib/api'
import CollectionsContainer from '@/containers/Collections'
import { createItemListSchema } from '@/utils/metadata'
import { setPageMetadata } from '@/utils/page-metadata'
import { t } from '@/i18n/translator'

export const Route = createFileRoute('/$locale/collections/')({
  // SSR prefetched data is treated as fresh forever so the client NEVER
  // re-runs these loaders on hydration. NOTE: must NOT use `Infinity` —
  // JSON.stringify(Infinity) === null, which TanStack treats as staleTime:0
  // and re-fetches. Number.MAX_SAFE_INTEGER serializes correctly and is
  // functionally "never stale" (~285,000 years).
  staleTime: Number.MAX_SAFE_INTEGER,
  loader: async ({ location }) => {
    // TanStack Router re-runs loaders on initial client hydration. On the
    // client, reuse SSR loader data from window.__SSR_COLLECTIONS__ instead
    // of re-fetching. This ensures ZERO client /api/v1/collections on
    // direct pageload.
    if (typeof window !== 'undefined' && (window as any).__SSR_COLLECTIONS__) {
      const cached = (window as any).__SSR_COLLECTIONS__
      delete (window as any).__SSR_COLLECTIONS__
      if (cached.initialCollections !== undefined) {
        return cached
      }
    }

    const searchParam = new URLSearchParams(location.search).get('q') ?? ''
    // Base /collections route: no `net` param per requirement (net is only
    // sent when a network route is active). Limit matches the page grid.
    const result = await getCollections({ limit: 24, sparam: searchParam })
    const collections = result.data || []
    const paginationData = result.pagination || { hasNext: false, hasPrev: false, page: 1, totalPages: 0 }

    const structuredData = createItemListSchema({
      title: t('meta.collections.title'),
      description: t('meta.collections.description'),
      url: 'https://stealthisnft.xyz/collections',
      items: (collections || []).slice(0, 20).map((c: any) => ({
        name: c.title || c.name || 'Collection',
        description: c.description || '',
        image: c.image || c.bannerImage || '',
        url: c.route_url || `https://stealthisnft.xyz/collection/${c.slug || c.id}`,
      })),
    })

    // setPageMetadata must run during loader execution (which happens before
    // renderToString) so entry-server.tsx can inject JSON-LD into SSR HTML.
    setPageMetadata({
      title: t('meta.collections.title'),
      description: t('meta.collections.description'),
      ogImage: '',
      ogType: 'website',
      twitterCard: 'summary_large_image',
      jsonLd: structuredData,
    })

    return { initialCollections: collections, initialPagination: paginationData, initialNetwork: 'any' }
  },
  component: Collections,
})

function Collections() {
  const { initialCollections, initialPagination, initialNetwork } = Route.useLoaderData() as {
    initialCollections: any[]
    initialPagination: any
    initialNetwork: string
  }
  return <CollectionsContainer initialCollections={initialCollections} initialPagination={initialPagination} initialNetwork={initialNetwork} initialSearch={''} />
}